import React, {useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import {Box, Button, Container, Heading, HStack, SimpleGrid, Stack, Text} from "@chakra-ui/react";
import {PhoneIcon} from "@chakra-ui/icons";
import {categories, categoryCover, imageUrl, photoCount} from "../data/catalog";
import {site} from "../data/site";
import PageBanner from "../components/PageBanner";
import SectionHeading from "../components/SectionHeading";
import SubCategoryCard from "../components/SubCategoryCard";
import CategoryTile from "../components/CategoryTile";
import PhotoMasonry from "../components/PhotoMasonry";
import Lightbox from "../components/Lightbox";
import {useQuote} from "../components/QuoteContext";
import usePageTitle from "../usePageTitle";

function CategoryPage({category}) {
    usePageTitle(category.title);
    const {openQuote} = useQuote();
    const [open, setOpen] = useState(null);

    // Categories with a single style show their photos straight away instead of one lonely card.
    const single = category.content.length === 1 ? category.content[0] : null;
    const photos = single ? single.img_urls.map(imageUrl) : [];
    const items = photos.map((src) => ({type: "image", src, title: single.description, subtitle: category.title}));
    const styles = category.content.length;
    const photoTotal = photoCount(category);

    return (
        <>
            <PageBanner
                image={imageUrl(categoryCover(category))}
                title={category.title}
                crumbs={[{label: "Home", to: "/"}, {label: "Products", to: "/#products"}, {label: category.title}]}
            >
                <Text>
                    {styles} {styles === 1 ? "style" : "styles"} · {photoTotal} project photos. Custom made, supplied and
                    professionally installed.
                </Text>
                <Stack direction={{base: "column", sm: "row"}} spacing={3} mt={7}>
                    <Button size="lg" onClick={() => openQuote(category.title)}>Get a free quote</Button>
                    <Button as="a" href={site.phoneHref} size="lg" variant="outline" colorScheme="whiteAlpha" color="white"
                            borderColor="whiteAlpha.700" leftIcon={<PhoneIcon/>} _hover={{bg: "whiteAlpha.200"}}>
                        {site.phone}
                    </Button>
                </Stack>
            </PageBanner>

            <Box as="section" py={{base: 12, md: 20}}>
                <Container maxW="container.xl">
                    {single ? (
                        <>
                            <Heading as="h2" fontSize={{base: "xl", md: "2xl"}} textTransform="uppercase" letterSpacing="0.03em" mb={8}>
                                {single.description}
                            </Heading>
                            <PhotoMasonry photos={photos} alt={single.description} onOpen={setOpen}/>
                        </>
                    ) : (
                        <>
                            <HStack justify="space-between" align="end" mb={8} flexWrap="wrap" gap={2}>
                                <Heading as="h2" fontSize={{base: "xl", md: "2xl"}} textTransform="uppercase" letterSpacing="0.03em">
                                    Choose a style
                                </Heading>
                                <Button as={RouterLink} to={`/gallery?category=${category.path}`} variant="link" colorScheme="brand" size="sm">
                                    All {category.title.toLowerCase()} photos
                                </Button>
                            </HStack>
                            <SimpleGrid columns={{base: 1, sm: 2, lg: 3}} spacing={6}>
                                {category.content.map((sub) => (
                                    <SubCategoryCard key={sub.path} category={category} sub={sub}/>
                                ))}
                            </SimpleGrid>
                        </>
                    )}
                </Container>
            </Box>

            <Box as="section" py={{base: 12, md: 20}} bg="ink.50">
                <Container maxW="container.xl">
                    <SectionHeading eyebrow="Explore" title="Other products" align="left" mb={8}/>
                    <SimpleGrid columns={{base: 2, md: 3, lg: 5}} spacing={3}>
                        {categories.filter((c) => c !== category).map((c) => (
                            <CategoryTile key={c.path} category={c} compact h={{base: "150px", md: "190px"}}/>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            <Lightbox items={items} index={open} onClose={() => setOpen(null)} onChange={setOpen}/>
        </>
    );
}

export default CategoryPage;
